// Story 022 — Split-view session state. Drives the overlay panel that
// mounts on top of the single-Viewer body and shows two image surfaces
// side-by-side. Story 029's linking workflow opens this in `link-defects`
// mode and uses the secondary pane as the link-target picker.
//
// Session-scoped (Zustand in-memory): the user's selection between
// "primary alone" and "split mode" should NOT persist across page
// reloads — pop-out windows are a separate concern (Story 048). Per-tab
// state is enough for the linking flow itself.
import { create } from "zustand";

export type SplitViewMode = "link-defects" | "compare";

export interface SplitViewState {
  /** Whether the split-view overlay is active. */
  active: boolean;
  /** Mode the overlay opened in. Drives the linking-affordance visibility. */
  mode: SplitViewMode;
  /** Primary image id — usually the currently-active image at open time. */
  primaryImageId: string | null;
  /** Secondary image id — `null` until the analyst picks a candidate. */
  secondaryImageId: string | null;
  /**
   * The defect the analyst selected on the secondary pane as the
   * link-target candidate. Story 029 reads this to enable the Link
   * button. Cleared when split-view closes OR when the secondary image
   * changes.
   */
  secondaryDefectId: string | null;
  /**
   * Defect id pinned as the "master" when the analyst opens split-view
   * in `link-defects` mode. Falls back to the active selection at open
   * time. Stays through secondary-image swaps so the analyst can hunt
   * for the right link target.
   */
  primaryDefectId: string | null;

  open: (input: {
    mode: SplitViewMode;
    primaryImageId: string;
    primaryDefectId?: string | null;
  }) => void;
  close: () => void;
  setSecondaryImage: (imageId: string | null) => void;
  setSecondaryDefect: (defectId: string | null) => void;
}

export const useSplitViewStore = create<SplitViewState>((set) => ({
  active: false,
  mode: "compare",
  primaryImageId: null,
  secondaryImageId: null,
  secondaryDefectId: null,
  primaryDefectId: null,
  open: ({ mode, primaryImageId, primaryDefectId }) => {
    set({
      active: true,
      mode,
      primaryImageId,
      secondaryImageId: null,
      secondaryDefectId: null,
      primaryDefectId: primaryDefectId ?? null,
    });
  },
  close: () => {
    set({
      active: false,
      mode: "compare",
      primaryImageId: null,
      secondaryImageId: null,
      secondaryDefectId: null,
      primaryDefectId: null,
    });
  },
  setSecondaryImage: (imageId) => {
    // Swapping the secondary image clears the previously-picked
    // candidate defect — the analyst is committing to a different
    // image to hunt in.
    set({
      secondaryImageId: imageId,
      secondaryDefectId: null,
    });
  },
  setSecondaryDefect: (defectId) => {
    set({ secondaryDefectId: defectId });
  },
}));
